import { useState } from "react";
import { Alert, Button, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { renderClose, useDialog } from "../../common/dialog";
import { createListStore, UseListStore } from "../../common/listStore";
import { ProductDto, ProjectDto } from "./common";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { ifOperators, ifTypes, ProjectMailOrderConditionDialog, triStateBoolOptions } from "./ProjectMailOrderConditionDialog";
import { Add, Delete, Edit } from "@mui/icons-material";
import { addOrUpdateItem, getLanguagesRaw } from "../../common/utils";
import { createAxios } from "../../common/axios";
import { combineLatest, map } from "rxjs";
import { formatNumber } from "../../common/localization";
import { useDefaultValues, DefaultValuesDto } from "../../common/defaultValues";
import { Dictionary, keyBy } from "lodash";
import { getGiftConditions, getTmPostageInfo, SaleArticleDetailDto, SaleArticleDto } from "../../common/order";
import { ProjectMailOrderConditionAddType, ProjectMailOrderConditionDto, ProjectMailOrderConditionIfType } from "../../common/types";
import { EnumItemDto, useCallCenters } from "../../common/enum";

export function ProjectMailOrderConditionListDialog(props: {
    close: () => void;
    product: ProductDto;
    project: ProjectDto,
    useProjectSaleArticleListStore: UseListStore<SaleArticleDetailDto>;
}) {
    const [useStore] = useState(() => createListStore<ProjectMailOrderConditionDto>({
        path: `products/projectMailOrderConditions?projectId=${props.project.id}`,
    }));

    const [useDisplayStore] = useState(() => createListStore({
        data: combineLatest([
            useDefaultValues.getState().clientData$,
            useStore.getState().items$,
            props.useProjectSaleArticleListStore.getState().items$.pipe(map(p => ({ dict: keyBy(p.items, p => p.id), isFetching: p.isFetching }))),
            useCallCenters.getState().items$.pipe(map(p => ({ dict: keyBy(p.items, p => p.id), isFetching: p.isFetching }))),
        ]).pipe(map(([def, conditions, articles, callCenters]) => ({

            items: conditions.items.map(p => ({ data: p, ...getConditionDescription(p, def.data, articles.dict, callCenters.dict) })),
            isFetching: conditions.isFetching || def.isFetching || articles.isFetching,
        }))),
        getId: p => p.data.id,
        listProps: [
            { column: "condition", label: "Condition", width: 400 },
            { column: "parcelShopSpecified", label: "Parcel shop?", width: 130 },
            { column: "addTypeDescription", label: "Add type", width: 125 },
            { column: "addArticle", label: "Add article", width: 400 },
            { column: "displayGroup", label: "Display position", width: 200 },
            { column: "selectionGroup", label: "Selection group", width: 120 },
        ]
    }));

    const { selectedCondition, isFetching } = useDisplayStore(p => ({
        selectedCondition: p.selectedItem?.data, isFetching: p.fetching,
    }));

    const moConds = useStore(p => ({ items: p.items, fetching: p.fetching }));

    const editDialog = useDialog(close => <ProjectMailOrderConditionDialog close={close} project={props.project} product={props.product!}
        useProjectSaleArticleListStore={props.useProjectSaleArticleListStore} onSaved={onConditionSaved} condition={selectedCondition!} />);
    const newDialog = useDialog(close => <ProjectMailOrderConditionDialog close={close} project={props.project} product={props.product!}
        useProjectSaleArticleListStore={props.useProjectSaleArticleListStore} onSaved={onConditionSaved} />);

    async function deleteCondition() {
        try {
            await createAxios().delete(`products/projectMailOrderConditions/${selectedCondition!.id}`);
        } finally {
            useStore.getState().fetch();
        }
    }

    function onConditionSaved(condition: ProjectMailOrderConditionDto) {
        addOrUpdateItem(useStore, p => p.id, condition);
    }

    const callCenters = useCallCenters(p => p.items);
    const def = useDefaultValues(p => p.clientData);
    const languagesRaw = getLanguagesRaw(def);
    const languages = languagesRaw.length === 1 ? [""] : languagesRaw;
    const isPostageCompatibleWithTmOrder = moConds.fetching ? null :
        callCenters.every(cc => languages.every(lang => getTmPostageInfo(moConds.items, cc.id as number, lang).error == null));
    const areAllGiftsCompatibleWithTmOrder = moConds.fetching ? null :
        getGiftConditions(moConds.items, null).length ===
        moConds.items.filter(p => p.addType === ProjectMailOrderConditionAddType.Article && (p.addDisc === 100 || p.addArticle?.price === 0)).length;

    return <>
        <DialogTitle>Project MO conditions</DialogTitle>
        {renderClose(props.close)}
        {editDialog.render()}
        {newDialog.render()}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item xs={6}><TextField label="Product" value={props.product.code + " " + props.product.name} disabled /></Grid>
                <Grid item xs={6}><TextField label="Project" value={props.project.code + " " + props.project.name} disabled /></Grid>
                <Grid item xs={12}>{renderListStore(useDisplayStore, {
                    onDoubleClick: () => editDialog.show(),
                    header: listHeaderItems([
                        ["edit", () => <Button color="edit" title="Edit" disabled={selectedCondition == null || isFetching} onClick={() => editDialog.show()}><Edit /></Button>],
                        ["add", () => <Button color="add" title="Add" disabled={isFetching} onClick={() => newDialog.show()}><Add /></Button>],
                        ["remove", () => <Button color="delete" disabled={selectedCondition == null || isFetching} title="Remove" onClick={deleteCondition}><Delete /></Button>],
                        ["isPostageCompatibleWithTmOrder", () => isPostageCompatibleWithTmOrder == null ? <></> : isPostageCompatibleWithTmOrder ?
                            <Alert sx={{ py: 0 }} severity="success">Postage is compatible with TM order.</Alert> :
                            <Alert sx={{ py: 0 }} severity="warning">Postage is not compatible with TM order.</Alert>],
                        ["areAllGiftsCompatibleWithTmOrder", () => areAllGiftsCompatibleWithTmOrder == null ? <></> : areAllGiftsCompatibleWithTmOrder ?
                            <Alert sx={{ py: 0 }} severity="success">All gifts are compatible with gift section in TM order.</Alert> :
                            <Alert sx={{ py: 0 }} severity="warning">Not all gifts are compatible with gift section in TM order.</Alert>],
                    ]),
                })}</Grid>
            </Grid>
        </DialogContent>
    </>;
}

function getConditionDescription(item: ProjectMailOrderConditionDto, def: DefaultValuesDto, articlesDict: Dictionary<SaleArticleDto>, callCentersDict: Dictionary<EnumItemDto>) {
    const landCode = def.landCode;
    const addArticle = articlesDict[item.addSaleArticleDetailId ?? ""];
    const ifArticle = articlesDict[item.ifSaleArticleDetailId ?? ""];
    const ifType = ifTypes.find(p => p.id === item.ifType);
    const mainCondition = item.ifType === ProjectMailOrderConditionIfType.Amount ?
        (ifType?.name + " ") +
        (ifOperators.find(p => p.id === item.ifOperator)?.name + " ") +
        formatNumber(item.ifAmount, landCode, true) :
        (ifType?.name + " ") +
        (item.ifQuantity === 1 ? "" : formatNumber(item.ifQuantity, landCode, false) + "x ") +
        (ifArticle == null ? "?" : ifArticle.code + " " + ifArticle.name + " (" + formatNumber(ifArticle.price, landCode, true) + ")");
    return {
        parcelShopSpecified: triStateBoolOptions.find(p => p.value === item.ifDeliveryMethodPlaceSpecified)?.label,
        addArticle: addArticle == null ? "?" : (item.addQuantity === 1 ? "" : formatNumber(item.addQuantity, landCode, false) + "x ") + addArticle.code + " " + addArticle.name + " (" + formatNumber(addArticle.price, landCode, true) + ")",
        addTypeDescription:
            item.addArticle?.isPost ? "Postage" :
                addArticle?.price === 0 ? "Free article" :
                    item.addDisc === 100 ? "Gift" :
                        item.addDisc !== 0 ? "Discounted " + formatNumber(item.addDisc, landCode, false) + "%" :
                            "Article",
        condition: mainCondition +
            (!item.ifLanguage ? "" : `, ${item.ifLanguage}`) +
            (item.ifCallCenterId == null ? "" : `, ${callCentersDict[item.ifCallCenterId]?.name ?? "???"}`),
        displayGroup: item.addDisc !== 100 ? "" : `${item.displayGroupPosition} "${item.displayGroup}" / ${item.positionInDisplayGroup}`,
        selectionGroup: item.addDisc !== 100 ? "" : item.selectionGroup,
    };
}
